import isEqual from 'lodash/isEqual';

import { extendPageLengthAction } from 'editor/src/store/design/slice';
import { DesignDimensions } from 'editor/src/store/design/types';
import getContentAreaFromSpread from 'editor/src/store/design/util/getContentAreaFromSpread';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import getFocusedContent from 'editor/src/store/editor/selector/getFocusedContent';
import getRollLength from 'editor/src/store/gangSheet/selector/getRollLength';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import { clampSheetLengthMm } from 'editor/src/util/gangSheet/rollingSheetLength';

// Sets the length of a rolling gang sheet, clamped to the host's limits and rounded up
// to a whole millimetre. Artwork never moves; only the sheet's bottom edge does. The
// design's `dimensions` always state the sheet's width, its current length and the
// length limits, because that is the size the host prices and orders — a product
// template that carries no dimensions gets them here. A no-op on a fixed-size sheet.
// Returns the length the sheet now has.
const setSheetLengthOperation =
  (lengthMm: number) =>
  (dispatch: ThunkDispatch, getState: () => RootState): number | undefined => {
    const state = getState();
    const rollLength = getRollLength(state);
    const { designData } = state.design;
    const spreadIndex = getCurrentSpreadIndex(state);
    const area = getContentAreaFromSpread(designData?.spreads[spreadIndex], getFocusedContent(state));
    if (!rollLength || !area || !designData) {
      return undefined;
    }

    const target = clampSheetLengthMm(lengthMm, rollLength);
    const deltaMm = target - area.height;
    const dimensions: DesignDimensions = {
      ...designData.dimensions,
      width: area.width,
      height: target,
      min_width: area.width,
      max_width: area.width,
      min_height: rollLength.minMm,
      max_height: rollLength.maxMm,
      max_surface: designData.dimensions?.max_surface,
    };
    if (Math.abs(deltaMm) > 1e-6 || !isEqual(dimensions, designData.dimensions)) {
      dispatch(extendPageLengthAction({ spreadIndex, deltaMm: Math.abs(deltaMm) > 1e-6 ? deltaMm : 0, dimensions }));
    }
    return target;
  };

export default setSheetLengthOperation;
